import { defineMessages, defineMessage } from '@formatjs/intl';
import deepEqual from 'fast-deep-equal';
import { binary_to_base58, base58_to_binary } from 'base58-js';
import { sha256 } from '@noble/hashes/sha256';
import * as schema from '../utils/schema';
import { urlPath, flattenFormattedParts } from '../utils/utils';
import { bytesToHex, hexToBytes } from '../utils/crypto';
import {
  formatInstrumentName,
  formatInstrumentTypeName,
  INSTRUMENT_TYPE_CURRENCY,
  INSTRUMENT_TYPE_CREDIT_NOTE,
  INSTRUMENT_TYPE_COUPON,
  INSTRUMENT_TYPE_LOYALTY,
  INSTRUMENT_TYPE_REWARD,
  INSTRUMENT_TYPE_CASINO,
  INSTRUMENT_TYPE_TICKET,
  INSTRUMENT_TYPE_FIXED_BOND,
  INSTRUMENT_TYPE_UNKNOWN,
  isInstrumentExpired,
  Instrument,
  CURRENCY_BSV,
  DEFAULT_CONTRACT_ACTION_FEE,
} from '../utils/instruments';
import { allowToFail, TokenizedApplicationError } from '../utils/errors';
import Service, { minutes, seconds } from './Service';
import {
  restApiContract,
  restApiInstrument,
  restApiContractTimelineEvent,
  restApiNewActivityCreated,
} from './TokenizedApiModelEntities';
import {
  DIFF_CONTRACT_DETAILS_FIELDS,
  RESET_INSTRUMENT_DETAILS_FIELDS,
  DIFF_INSTRUMENT_TYPE_SPECIFICS_FIELDS,
} from '../utils/contracts/contractAmendmentDiffs';

const FORMAT_CONTRACT_NAME_UNTITLED_DEFAULT = defineMessage({
  id: 'TokenizedSDK:contract.name.untitled.type',
  description: 'Untitled instrument contract name, unrecognized type',
  defaultMessage: 'Untitled {contract_type} instrument contract',
});
const FORMAT_CONTRACT_NAME_UNTITLED = defineMessages({
  [INSTRUMENT_TYPE_CURRENCY]: {
    id: 'TokenizedSDK:contract.name.untitled.currency',
    description: 'Untitled instrument contract name: Currency',
    defaultMessage: 'Untitled currency contract',
  },
  [INSTRUMENT_TYPE_CREDIT_NOTE]: {
    id: 'TokenizedSDK:contract.name.untitled.creditNote',
    description: 'Untitled instrument contract name: Credit note',
    defaultMessage: 'Untitled credit note contract',
  },
  [INSTRUMENT_TYPE_COUPON]: {
    id: 'TokenizedSDK:contract.name.untitled.discountCoupon',
    description: 'Untitled instrument contract name: Discount coupon',
    defaultMessage: 'Untitled discount coupon contract',
  },
  [INSTRUMENT_TYPE_LOYALTY]: {
    id: 'TokenizedSDK:contract.name.untitled.loyaltyPoint',
    description: 'Untitled instrument contract name: Loyalty point',
    defaultMessage: 'Untitled loyalty point contract',
  },
  [INSTRUMENT_TYPE_REWARD]: {
    id: 'TokenizedSDK:contract.name.untitled.rewardPoint',
    description: 'Untitled instrument contract name: Reward point',
    defaultMessage: 'Untitled reward point contract',
  },
  [INSTRUMENT_TYPE_CASINO]: {
    id: 'TokenizedSDK:contract.name.untitled.casinoChip',
    description: 'Untitled instrument contract name: Casino chip',
    defaultMessage: 'Untitled casino chip contract',
  },
  [INSTRUMENT_TYPE_TICKET]: {
    id: 'TokenizedSDK:contract.name.untitled.eventTicket',
    description: 'Untitled instrument contract name: Event ticket',
    defaultMessage: 'Untitled event ticket contract',
  },
  [INSTRUMENT_TYPE_FIXED_BOND]: {
    id: 'TokenizedSDK:contract.name.untitled.fixedRateBond',
    description: 'Untitled instrument contract name: Fixed rate bond',
    defaultMessage: 'Untitled fixed rate bond contract',
  },
  [INSTRUMENT_TYPE_UNKNOWN]: {
    id: 'TokenizedSDK:contract.name.untitled',
    description: 'Untitled instrument contract name, no type',
    defaultMessage: 'Untitled instrument contract',
  },
});

const FORMAT_CONTRACT_TYPE_DEFAULT = defineMessage({
  id: 'TokenizedSDK:contract.type',
  description: 'Instrument contract type, unrecognized type',
  defaultMessage: '{contract_type} instrument contract',
});
const FORMAT_CONTRACT_TYPE = defineMessages({
  [INSTRUMENT_TYPE_CURRENCY]: {
    id: 'TokenizedSDK:contract.type.currency',
    description: 'Instrument contract type: Currency',
    defaultMessage: 'Currency contract',
  },
  [INSTRUMENT_TYPE_CREDIT_NOTE]: {
    id: 'TokenizedSDK:contract.type.creditNote',
    description: 'Instrument contract type: Credit note',
    defaultMessage: 'Credit note contract',
  },
  [INSTRUMENT_TYPE_COUPON]: {
    id: 'TokenizedSDK:contract.type.discountCoupon',
    description: 'Instrument contract type: Discount coupon',
    defaultMessage: 'Discount coupon contract',
  },
  [INSTRUMENT_TYPE_LOYALTY]: {
    id: 'TokenizedSDK:contract.type.loyaltyPoint',
    description: 'Instrument contract type: Loyalty point',
    defaultMessage: 'Loyalty point contract',
  },
  [INSTRUMENT_TYPE_REWARD]: {
    id: 'TokenizedSDK:contract.type.rewardPoint',
    description: 'Instrument contract type: Reward point',
    defaultMessage: 'Reward point contract',
  },
  [INSTRUMENT_TYPE_CASINO]: {
    id: 'TokenizedSDK:contract.type.casinoChip',
    description: 'Instrument contract type: Casino chip',
    defaultMessage: 'Casino chip contract',
  },
  [INSTRUMENT_TYPE_TICKET]: {
    id: 'TokenizedSDK:contract.type.eventTicket',
    description: 'Instrument contract type: Event ticket',
    defaultMessage: 'Event ticket contract',
  },
  [INSTRUMENT_TYPE_FIXED_BOND]: {
    id: 'TokenizedSDK:contract.type.fixedRateBond',
    description: 'Instrument contract type: Fixed rate bond',
    defaultMessage: 'Fixed rate bond contract',
  },
  [INSTRUMENT_TYPE_UNKNOWN]: {
    id: 'TokenizedSDK:contract.type.untitled',
    description: 'Instrument contract type unknown',
    defaultMessage: 'Instrument contract',
  },
});

const FORMAT_CONTRACT_INSTRUMENTS_SUMMARY_DEFAULT = defineMessage({
  id: 'TokenizedSDK:contract.instrumentSummary',
  description: 'Contract instrument definitions summary, unrecognized type',
  defaultMessage:
    '{count, plural, one {# {contract_type} instrument} other {# {contract_type} instruments}}',
});
const FORMAT_CONTRACT_INSTRUMENTS_SUMMARY = defineMessages({
  [INSTRUMENT_TYPE_CURRENCY]: {
    id: 'TokenizedSDK:contract.instrumentSummary.currency',
    description: 'Contract instrument definition summary: Currency',
    defaultMessage:
      '{count, plural, one {# currency instrument} other {# currency instruments}}',
  },
  [INSTRUMENT_TYPE_CREDIT_NOTE]: {
    id: 'TokenizedSDK:contract.instrumentSummary.creditNote',
    description: 'Contract instrument definition summary: Credit note',
    defaultMessage:
      '{count, plural, one {# credit note instrument} other {# credit note instruments}}',
  },
  [INSTRUMENT_TYPE_COUPON]: {
    id: 'TokenizedSDK:contract.instrumentSummary.discountCoupon',
    description: 'Contract instrument definition summary: Discount coupon',
    defaultMessage:
      '{count, plural, one {# discount coupon instrument} other {# discount coupon instruments}}',
  },
  [INSTRUMENT_TYPE_LOYALTY]: {
    id: 'TokenizedSDK:contract.instrumentSummary.loyaltyPoint',
    description: 'Contract instrument definition summary: Loyalty point',
    defaultMessage:
      '{count, plural, one {# loyalty point instrument} other {# loyalty point instruments}}',
  },
  [INSTRUMENT_TYPE_REWARD]: {
    id: 'TokenizedSDK:contract.instrumentSummary.rewardPoint',
    description: 'Contract instrument definition summary: Reward point',
    defaultMessage:
      '{count, plural, one {# reward point instrument} other {# reward point instruments}}',
  },
  [INSTRUMENT_TYPE_CASINO]: {
    id: 'TokenizedSDK:contract.instrumentSummary.casinoChip',
    description: 'Contract instrument definition summary: Casino chip',
    defaultMessage:
      '{count, plural, one {# casino chip instrument} other {# casino chip instruments}}',
  },
  [INSTRUMENT_TYPE_TICKET]: {
    id: 'TokenizedSDK:contract.instrumentSummary.eventTicket',
    description: 'Contract instrument definition summary: Event ticket',
    defaultMessage:
      '{count, plural, one {# event ticket instrument} other {# event ticket instruments}}',
  },
  [INSTRUMENT_TYPE_FIXED_BOND]: {
    id: 'TokenizedSDK:contract.instrumentSummary.fixedRateBond',
    description: 'Contract instrument definition summary: Fixed rate bond',
    defaultMessage:
      '{count, plural, one {# fixed rate bond instrument} other {# fixed rate bond instruments}}',
  },
  [INSTRUMENT_TYPE_UNKNOWN]: {
    id: 'TokenizedSDK:contract.instrumentSummary.untitled',
    description: 'Contract instrument definition summary: type unknown',
    defaultMessage:
      '{count, plural, one {# instrument instrument} other {# instrument instruments}}',
  },
});

/** @category SDK Modules */
class Contracts extends Service {
  contractsQuery = this.makeQuery(
    (query, { profileId } = {}) => ({
      ...query,
      staleTime: seconds(60),
      enabled: query.enabled && !!profileId,
      notifyOnChangeProps: ['data'],
    }),
    async (context, { profileId } = {}) => {
      if (!profileId) {
        return null;
      }

      const [response, profiles] = await Promise.all([
        this.tokenizedApi.endpoints.get(
          urlPath('profiles', profileId, 'contracts'),
        ),
        allowToFail(
          () => this.tokenizedApi.entities.profilesQuery.fetch(),
          'contracts getting profiles',
        ),
      ]);

      const profile = profiles?.idMap?.get?.(profileId);
      const entity = await allowToFail(
        () =>
          this.tokenizedApi.entities.entityQuery.fetch(
            profileId,
            profile?.entityId,
          ),
        'contracts getting entity',
      );

      const normalized = schema.normalizeResponse(
        'Query contracts',
        response?.data?.data,
        schema.arrayOf(restApiContract),
      );

      const idMap = new Map();
      const contracts = (normalized.normalizedValue || []).map((contract) => {
        const transformedContract = this.transformContract(contract, {
          profile,
          entity,
        });
        if (transformedContract?.draft?.id) {
          idMap.set(transformedContract.draft.id, transformedContract);
        }
        if (transformedContract?.contractAddress) {
          idMap.set(transformedContract.contractAddress, transformedContract);
        }
        return transformedContract;
      });

      return {
        contracts,
        idMap,
        clauseIdMap: normalized.modelEntities.clause,
      };
    },
  );

  contractQuery = this.makeQuery(
    (query, { profileId, contractId } = {}) => ({
      ...query,
      staleTime: seconds(60),
      enabled: query.enabled && !!profileId && !!contractId,
    }),
    async (context, { profileId, contractId } = {}) => {
      if (!contractId || !profileId) {
        return null;
      }

      try {
        const [response, { profile, entity } = {}] = await Promise.all([
          this.tokenizedApi.endpoints.get(
            urlPath('profiles', profileId, 'contracts', contractId),
          ),
          allowToFail(async () => {
            const profiles =
              await this.tokenizedApi.entities.profilesQuery.fetch();
            const profile = profiles.idMap.get(profileId);
            const entity = await this.tokenizedApi.entities.entityQuery.fetch(
              profileId,
              profile.entityId,
            );
            return {
              profile,
              entity,
            };
          }, 'contract getting profile/entity'),
        ]);

        const contract = schema.normalizeResponse(
          'Query contract',
          response?.data?.data,
          restApiContract,
        ).normalizedValue;

        const transformedContract = this.transformContract(contract, {
          profile,
          entity,
        });

        return transformedContract;
      } catch (e) {
        if (e instanceof TokenizedApplicationError) {
          return null;
        }
        throw e;
      }
    },
  );

  contractTimelineQuery = this.makeQuery(
    (query, { profileId, contractId } = {}) => ({
      ...query,
      staleTime: minutes(15),
      enabled: query.enabled && !!profileId && !!contractId,
    }),
    async (context, { profileId, contractId } = {}) => {
      if (!profileId || !contractId) {
        return null;
      }
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'contracts', contractId, 'history'),
      );
      const { normalizedValue: events } = schema.normalizeResponse(
        'Query contract timeline',
        response.data.data,
        schema.arrayOf(restApiContractTimelineEvent),
      );
      const instrumentQuantities = new Map();
      return events.reduceRight((transformedEvents, event) => {
        if (event.instrumentId) {
          const prevQuantity =
            instrumentQuantities.get(event.instrumentId) || 0;
          let newQuantity = prevQuantity;
          if (event.eventType === 'instrument_expire') {
            newQuantity = 0;
          } else if (
            event.actionCode === 'I2' &&
            typeof event.actionDetails?.authorizedQuantity === 'number'
          ) {
            newQuantity = event.actionDetails.authorizedQuantity;
          }
          instrumentQuantities.set(event.instrumentId, newQuantity);
          event = {
            ...event,
            authorizedQuantity: {
              delta: newQuantity - prevQuantity,
              total: newQuantity,
            },
          };
        }
        return [this.transformTimelineEvent(event), ...transformedEvents];
      }, []);
    },
  );

  contractInstrumentsQuery = this.makeQuery(
    (query, { profileId, contractId } = {}) => ({
      ...query,
      staleTime: seconds(60),
      enabled: query.enabled && !!profileId && !!contractId,
    }),
    async (context, { profileId, contractId } = {}) => {
      if (!contractId || !profileId) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'contracts', contractId, 'instruments'),
      );

      const normalized = schema.normalizeResponse(
        'Query contract instruments',
        response?.data?.data,
        schema.arrayOf(restApiInstrument),
      );

      const instruments = (normalized.normalizedValue || []).map((instrument) =>
        this.transformInstrument(instrument),
      );

      const idMap = new Map();
      instruments.forEach((instrument) => {
        if (instrument?.instrumentId) {
          idMap.set(instrument.instrumentId, instrument);
        }
        if (instrument?.draft?.id) {
          idMap.set(instrument.draft.id, instrument);
        }
      });

      return {
        instruments,
        idMap,
      };
    },
  );

  transformContract(contract, { entity }) {
    if (!contract) {
      return contract;
    }

    const isPosted = !!contract.details;
    const isDraft = !contract.details;
    let isExpired = false;
    if (isPosted && contract.details.contractExpiration) {
      isExpired = contract.details.contractExpiration < Date.now();
    }
    let isOwner = false;
    if (entity?.handle && contract?.issuerHandle === entity.handle) {
      isOwner = true;
    }

    return {
      ...contract,
      isPosted,
      isDraft,
      isExpired,
      isOwner,
      isEditable: isOwner && !isExpired,
      formatted: {
        formatName: this.makeFormatContractName(contract),
        formatType: this.makeFormatContractType(contract),
        formatInstrumentSummary:
          this.makeFormatContractInstrumentSummary(contract),
      },
    };
  }

  transformInstrument(instrument) {
    if (!instrument) {
      return instrument;
    }

    const isPosted = !!instrument.details;
    const isDraft = !instrument.details;
    let isExpired = false;
    if (isPosted) {
      isExpired = isInstrumentExpired(instrument);
    }

    return {
      ...instrument,
      isPosted,
      isDraft,
      isExpired,
      formatted: {
        formatName: this.makeFormatInstrumentName(instrument),
        formatType: this.makeFormatInstrumentType(instrument),
      },
    };
  }

  filterContracts({
    contracts = [],
    includeActiveContracts = true,
    includeDraftContracts = true,
    includeEndedContracts = true,
    filterOwnedContracts = undefined,
  } = {}) {
    return contracts
      .filter(({ isPosted, isDraft, isExpired, isOwner } = {}) => {
        if (
          (isOwner && filterOwnedContracts === false) ||
          (!isOwner && filterOwnedContracts === true)
        ) {
          return false;
        }
        if (isPosted && !isExpired) {
          return includeActiveContracts;
        } else if (isDraft) {
          return includeDraftContracts;
        } else if (isOwner) {
          return includeEndedContracts;
        }
        return false;
      })
      .sort((a, b) => {
        const aDate = a?.draft?.dateModified || a?.dateModified;
        const bDate = b?.draft?.dateModified || b?.dateModified;
        return aDate && bDate ? bDate - aDate : 0;
      });
  }

  instrumentsNeededForContractPreparation(contract, contractInstruments) {
    const instrumentIds = new Set([CURRENCY_BSV]);

    contractInstruments?.forEach?.((contractInstrument) => {
      if (contractInstrument.instrumentId) {
        instrumentIds.add(contractInstrument.instrumentId);
      }
      if (contractInstrument.draft?.id) {
        instrumentIds.add(contractInstrument.draft.id);
      }
      if (contractInstrument.instrumentTypeSpecifics?.currencyCode) {
        instrumentIds.add(
          contractInstrument.instrumentTypeSpecifics.currencyCode,
        );
      }
      if (contractInstrument.faceValue?.currency) {
        instrumentIds.add(contractInstrument.faceValue.currency);
      }
      if (contractInstrument.instrumentTypeSpecifics?.faceValue?.currency) {
        instrumentIds.add(
          contractInstrument.instrumentTypeSpecifics.faceValue.currency,
        );
      }
      if (
        contractInstrument.draft?.instrumentTypeSpecifics?.faceValue?.currency
      ) {
        instrumentIds.add(
          contractInstrument.draft.instrumentTypeSpecifics.faceValue.currency,
        );
      }
    });

    return instrumentIds;
  }

  prepareContractForRender({
    contract,
    contractInstruments,
    otherInstrumentsById,
    intl,
  } = {}) {
    if (!contract) {
      return contract;
    }

    let preparedContract = {
      ...contract,
      instruments: contractInstruments,
      formatted: {
        ...contract.formatted,
        name: contract.formatted.formatName?.({ intl }),
        draftName: contract.formatted.formatName?.({
          intl,
          preferDraftName: true,
        }),
        type: contract.formatted.formatType?.(intl),
        instrumentSummary: contract.formatted.formatInstrumentSummary({
          intl,
          count: contractInstruments?.length,
        }),
        actionFee: otherInstrumentsById?.get(CURRENCY_BSV)?.withQuantity({
          quantity: contract.isDraft
            ? contract.draft?.details?.contractFee
            : contract.details?.contractFee,
        }),
      },
    };

    // Instrument formatting
    preparedContract = {
      ...preparedContract,
      instruments:
        preparedContract?.instruments &&
        preparedContract.instruments.map((instrument) => ({
          ...instrument,
          faceValue: this.convertFaceValueToInstrumentQuantity(
            instrument.faceValue,
            otherInstrumentsById,
          ),
          details: instrument.details && {
            ...instrument.details,
            ...this.convertTransferFeeToInternalValues(
              'transferFee',
              instrument.details.transferFee,
              instrument.draft?.id || instrument.instrumentId,
              otherInstrumentsById,
            ),
          },
          instrumentTypeSpecifics: instrument.instrumentTypeSpecifics && {
            ...instrument.instrumentTypeSpecifics,
            faceValue: this.convertFaceValueToInstrumentQuantity(
              instrument.instrumentTypeSpecifics.faceValue,
              otherInstrumentsById,
            ),
          },
          draft: instrument.draft && {
            ...instrument.draft,
            details: instrument.draft.details && {
              ...instrument.draft.details,
              ...this.convertTransferFeeToInternalValues(
                'transferFee',
                instrument.draft.details.transferFee,
                instrument.draft.id,
                otherInstrumentsById,
              ),
            },
            instrumentTypeSpecifics: instrument.draft
              .instrumentTypeSpecifics && {
              ...instrument.draft.instrumentTypeSpecifics,
              faceValue: this.convertFaceValueToInstrumentQuantity(
                instrument.draft.instrumentTypeSpecifics.faceValue,
                otherInstrumentsById,
              ),
            },
          },
          formatted: {
            ...instrument.formatted,
            formatName: (options) =>
              instrument.formatted?.formatName?.({
                ...options,
                intl,
                otherInstrumentsById,
              }),
            name: instrument.formatted?.formatName?.({
              intl,
              otherInstrumentsById,
            }),
            draftName: instrument.formatted?.formatName?.({
              intl,
              otherInstrumentsById,
              preferDraftName: true,
            }),
            type: instrument.formatted?.formatType?.(intl),
          },
        })),
    };

    // Instrument ID map
    const instrumentsById = new Map();
    preparedContract.instruments?.forEach?.((instrument) => {
      if (instrument?.instrumentId) {
        instrumentsById.set(instrument.instrumentId, instrument);
      }
      if (instrument?.draft?.id) {
        instrumentsById.set(instrument.draft.id, instrument);
      }
    });
    preparedContract = { ...preparedContract, instrumentsById };

    return preparedContract;
  }

  // This allows fully-functional Instrument objects to be constructed from
  // contract instrument draft IDs (the API will not supply instrument details
  // for a draft ID)
  setInstrumentDetailsFromContractDraft(instrument) {
    if (instrument?.isDraft && instrument?.instrumentId) {
      // Unfortuantely there isn’t currently a way in the API
      // to go from a draft instrument ID to a contract address,
      // so the best we can do for now is to search through all
      // the draft instrument IDs in the cache
      const client = this.tokenizedApi.getQueryClient();
      const knownContracts = client.getQueriesData(
        this.contractQuery.makeCacheKey(),
      );
      const knownContractInstruments = client.getQueriesData(
        this.contractInstrumentsQuery.makeCacheKey(),
      );
      const contractInstrument = knownContractInstruments
        ?.find(([, { idMap } = {}]) => idMap?.has(instrument.instrumentId))?.[1]
        ?.idMap?.get(instrument.instrumentId);
      const draftContractId = contractInstrument?.draft?.contractId;
      const contract = knownContracts?.find(
        ([, knownContract]) =>
          knownContract?.draft?.id &&
          knownContract?.draft?.id === draftContractId,
      )?.[1];

      if (contractInstrument) {
        return instrument.withInstrumentDetails({
          instrumentId: instrument.instrumentId,
          contractAddress: contractInstrument.contractAddress,
          instrumentType: contractInstrument.draft?.details?.instrumentType,
          freezePeriod: contractInstrument.freezePeriod,
          freezeTxId: contractInstrument.freezeTxId,
          authorizedQuantity:
            contractInstrument.draft?.details?.authorizedQuantity,
          instrumentTypeSpecifics:
            contractInstrument.draft?.instrumentTypeSpecifics,
          contractFee: contract?.draft?.details?.contractFee,
          transferFee: contractInstrument.draft?.details?.transferFee?.quantity,
          dateCreated: contractInstrument.draft?.dateCreated,
          dateModified: contractInstrument.draft?.dateModified,
        });
      }
    }
    return instrument;
  }

  convertFaceValueToInstrumentQuantity(faceValue, instrumentsById) {
    if (!faceValue?.currency) {
      return faceValue;
    }
    const currencyInstrument = instrumentsById?.get?.(faceValue.currency);
    if (!currencyInstrument) {
      return faceValue;
    }
    return currencyInstrument.withQuantity({
      quantity: faceValue.units,
      precision: faceValue.unitPrecision,
    });
  }

  convertInstrumentQuantityToFaceValue(quantity) {
    if (Instrument.isInstrument(quantity)) {
      return {
        currency: quantity.currencyCode,
        units: quantity.quantity,
        unitPrecision: quantity.precision,
      };
    }
    return quantity;
  }

  convertLockingScriptToAddress(hexAddress) {
    try {
      const lockingScriptBytes = hexToBytes(hexAddress);
      switch (lockingScriptBytes[0]) {
        case 0x20:
          if (lockingScriptBytes.length === 21) {
            const keyHash = lockingScriptBytes.slice(1);
            const withVersion = new Uint8Array([0, ...keyHash]);
            const checksum = sha256(sha256(withVersion)).slice(0, 4);
            return binary_to_base58(
              new Uint8Array([...withVersion, ...checksum]),
            );
          }
          break;
        case 0x25: {
          const encodedAddress = `bitcoin-script:0101${hexAddress.slice(2)}`;
          const addressBytes = new TextEncoder().encode(encodedAddress);
          const checksum = sha256(sha256(addressBytes)).slice(0, 4);
          return `${encodedAddress}${bytesToHex(checksum)}`;
        }
      }
    } catch (error) {
      return null;
    }
    return null;
  }

  convertAddressToLockingScript(address) {
    try {
      if (address.startsWith('bitcoin-script:')) {
        const scriptBytes = address.slice(19, -8);
        if (!scriptBytes) {
          return null;
        }
        return `25${scriptBytes}`;
      }

      const decoded = base58_to_binary(address);
      if (decoded.length === 25 && decoded[0] === 0) {
        const checksum = decoded.slice(decoded.length - 4, decoded.length);
        const body = decoded.slice(0, decoded.length - 4);
        const expectedChecksum = sha256(sha256(body)).slice(0, 4);
        if (checksum.every((byte, index) => expectedChecksum[index] === byte)) {
          const rawAddress = new Uint8Array([0x20, ...body.slice(1)]);
          return bytesToHex(rawAddress);
        }
      }
    } catch (error) {
      return null;
    }
    return null;
  }

  convertTransferFeeToInternalValues(
    name,
    transferFee,
    instrumentId,
    instrumentsById,
  ) {
    const result = {};

    const feePaidInInstrumentId = transferFee?.useCurrentInstrument
      ? instrumentId
      : CURRENCY_BSV;
    const feePaidInInstrument = instrumentsById?.get(feePaidInInstrumentId);
    result[`${name}.amount`] = feePaidInInstrument;
    if (typeof transferFee?.quantity === 'number') {
      const instrumentWithQuantity = feePaidInInstrument?.withQuantity({
        quantity: transferFee.quantity,
      });
      if (instrumentWithQuantity) {
        result[`${name}.amount`] = instrumentWithQuantity;
      }
    }
    if (typeof transferFee?.address === 'string' && transferFee.address) {
      const formattedAddress = this.convertLockingScriptToAddress(
        transferFee.address,
      );
      if (formattedAddress) {
        result[`${name}.address`] = formattedAddress;
      } else {
        // For locking scripts in formats we don’t support,
        // show the raw hex value
        result[`${name}.address`] = transferFee.address;
      }
    }

    return result;
  }

  convertInternalValuesToTransferFee(name, instrumentId, values) {
    const result = {
      Quantity: null,
      UseCurrentInstrument: false,
      Address: null,
    };
    if (`${name}.amount` in values) {
      const instrumentAmount = values[`${name}.amount`];
      if (Instrument.isInstrument(instrumentAmount)) {
        if (instrumentAmount.instrumentId === instrumentId) {
          result.UseCurrentInstrument = true;
        }
        if (
          instrumentAmount.hasQuantity &&
          (instrumentAmount.instrumentId === instrumentId ||
            instrumentAmount.isBsv)
        ) {
          result.Quantity = instrumentAmount.quantity;
        }
      }
    } else {
      result.Quantity = values[name]?.quantity || null;
      result.UseCurrentInstrument = values[name]?.useCurrentInstrument || false;
    }

    if (!(result.Quantity > 0)) {
      // Clearing the quantity also clears the address
      return result.UseCurrentInstrument ? result : null;
    }

    if (`${name}.address` in values) {
      if (
        typeof values[`${name}.address`] === 'string' &&
        values[`${name}.address`]
      ) {
        const hexAddress = this.convertAddressToLockingScript(
          values[`${name}.address`],
        );
        if (hexAddress) {
          result.Address = hexAddress;
        } else {
          // If the specified address is invalid, revert to the previous value
          result.Address = values[name]?.address || null;
        }
      } else {
        // If input is blank, clear the draft address
        result.Address = null;
      }
    } else {
      result.Address = values[name]?.address || null;
    }

    if (result.Quantity || result.Address || result.UseCurrentInstrument) {
      return result;
    }
    return null;
  }

  convertValidDateToNanosecondTimestamp(value) {
    if (
      value === undefined ||
      value === null ||
      value === false ||
      value === true
    ) {
      return null;
    }
    let timestamp;
    try {
      timestamp = new Date(value).getTime() * 1e6;
    } catch (error) {
      return null;
    }
    if (isNaN(timestamp)) {
      return null;
    }
    return timestamp;
  }

  onActivity(profileId) {
    if (profileId) {
      this.contractsQuery.invalidate({ profileId });
      // TODO: Need a mechanism to only update contracts that have changed
      this.contractQuery.invalidate({ profileId });
      this.contractInstrumentsQuery.invalidate({ profileId });
      this.contractTimelineQuery.invalidate({ profileId });
    } else {
      // Update all profiles if we don’t know which one it was
      this.contractsQuery.invalidate();
      this.contractQuery.invalidate();
      this.contractTimelineQuery.invalidate();
    }
  }

  async createContract(profileId, lockboxId, contractType) {
    const response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'contracts'),
      { lock_box_id: lockboxId },
    );

    this.contractsQuery.invalidate(profileId);

    const newContract = schema.normalizeResponse(
      'Create contract',
      response?.data?.data,
      restApiContract,
    ).normalizedValue;

    const contractId = newContract?.draft?.id;
    if (!contractId) {
      throw new Error('Create contract failed');
    }

    // Update contract with defaults, which also
    // populates the query cache with the individual contract data
    await this.updateContractOrInstrumentFields({
      profileId,
      contractId,
      fieldValues: {
        governingLaw: 'USA  ',
        jurisdiction: 'USA  ',
      },
    });

    // Create the first instrument, which sets the contract type
    await this.createContractInstrument(profileId, contractId, contractType);

    return contractId;
  }

  async createContractInstrument(profileId, contractId, instrumentType) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }

    if (!instrumentType) {
      instrumentType = contract?.contractType;
      if (!instrumentType) {
        // Fallback default
        instrumentType = INSTRUMENT_TYPE_COUPON;
      }
    }

    const response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'contracts', contractId, 'instruments'),
      {},
    );

    this.contractInstrumentsQuery.invalidate({ profileId, contractId });
    this.contractTimelineQuery.invalidate({ profileId, contractId });

    const newInstrument = schema.normalizeResponse(
      'Create contract instrument',
      response?.data?.data,
      restApiInstrument,
    ).normalizedValue;

    const instrumentId = newInstrument?.draft?.id;
    if (!instrumentId) {
      throw new Error('Create contract instrument failed');
    }

    // Set the instrument type, which also
    // populates the query cache with the instrument data
    await this.updateContractOrInstrumentFields({
      profileId,
      contractId,
      instrumentId,
      fieldSectionName: 'details',
      fieldValues: {
        instrumentType: instrumentType,
      },
    });
    // Setting the type of the first instrument updates the contract’s type
    this.contractQuery.invalidate({ profileId, contractId });

    // Type-specific defaults
    if (instrumentType === INSTRUMENT_TYPE_CASINO) {
      await this.updateContractOrInstrumentFields({
        profileId,
        contractId,
        instrumentId,
        fieldSectionName: 'typeSpecifics',
        fieldValues: {
          useType: 'R',
        },
      });
    }
    if (instrumentType === INSTRUMENT_TYPE_CREDIT_NOTE) {
      await this.updateContractOrInstrumentFields({
        profileId,
        contractId,
        instrumentId,
        fieldSectionName: 'typeSpecifics',
        fieldValues: {
          faceValue: new Instrument({
            currencyCode: 'USD',
            precision: 2,
            quantity: 1,
          }),
        },
      });
    }

    return instrumentId;
  }

  async updateContractOrInstrumentFields({
    profileId,
    contractId,
    instrumentId,
    fieldSectionName,
    fieldValues,
  } = {}) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }

    if (instrumentId) {
      const currentInstrumentsData =
        await this.contractInstrumentsQuery.fetchWithOptions(
          { staleTime: minutes(5) },
          { profileId, contractId },
        );
      const instrument = currentInstrumentsData?.idMap?.get?.(instrumentId);
      if (instrument.isExpired) {
        throw new Error('Instrument has expired');
      }

      let updatedDetails = instrument?.draft?.details || {};
      let updatedInstrumentTypeSpecifics =
        instrument?.draft?.instrumentTypeSpecifics || {};

      if (fieldSectionName === 'typeSpecifics') {
        updatedInstrumentTypeSpecifics = {
          ...updatedInstrumentTypeSpecifics,
          ...fieldValues,
        };
      } else {
        updatedDetails = {
          ...updatedDetails,
          ...fieldValues,
        };
      }

      const requestDetails = {
        // These fields can be modified
        AuthorizedTokenQty: updatedDetails.authorizedQuantity,
        TradeRestrictions: updatedDetails.tradeRestrictions,
        InstrumentType: updatedDetails.instrumentType,
        TransferFee: this.convertInternalValuesToTransferFee(
          'transferFee',
          instrument?.draft?.id,
          updatedDetails,
        ),
        // These fields are read only for now, so get copied directly from the original API response
        InstrumentPermissions:
          updatedDetails[schema.apiDataKey]?.InstrumentPermissions,
        EnforcementOrdersPermitted:
          updatedDetails[schema.apiDataKey]?.EnforcementOrdersPermitted,
        VotingRights: updatedDetails[schema.apiDataKey]?.VotingRights,
        VoteMultiplier: updatedDetails[schema.apiDataKey]?.VoteMultiplier,
        AdministrationProposal:
          updatedDetails[schema.apiDataKey]?.AdministrationProposal,
        HolderProposal: updatedDetails[schema.apiDataKey]?.HolderProposal,
        InstrumentModificationGovernance:
          updatedDetails[schema.apiDataKey]?.InstrumentModificationGovernance,
        InstrumentPayload: updatedDetails[schema.apiDataKey]?.InstrumentPayload,
      };

      let requestTypeSpecifics;
      switch (updatedDetails.instrumentType) {
        case INSTRUMENT_TYPE_CREDIT_NOTE: {
          const faceValue = this.convertInstrumentQuantityToFaceValue(
            updatedInstrumentTypeSpecifics.faceValue,
          );

          requestTypeSpecifics = {
            Name: updatedInstrumentTypeSpecifics.Name,
            FaceValue: {
              Value: faceValue?.units,
              CurrencyCode: faceValue?.currency,
              Precision: faceValue?.unitPrecision,
            },
            ExpirationTimestamp: this.convertValidDateToNanosecondTimestamp(
              updatedInstrumentTypeSpecifics.expirationTimestamp,
            ),
            TransfersPermitted:
              updatedInstrumentTypeSpecifics.transfersPermitted,
          };
          break;
        }
        case INSTRUMENT_TYPE_COUPON:
          requestTypeSpecifics = {
            CouponName: updatedInstrumentTypeSpecifics.couponName,
            RedemptionVenue: updatedInstrumentTypeSpecifics.redemptionVenue,
            Details: updatedInstrumentTypeSpecifics.details,
            ExpirationTimestamp: this.convertValidDateToNanosecondTimestamp(
              updatedInstrumentTypeSpecifics.expirationTimestamp,
            ),
            TransfersPermitted:
              updatedInstrumentTypeSpecifics.transfersPermitted,
            ValidFromTimestamp: this.convertValidDateToNanosecondTimestamp(
              updatedInstrumentTypeSpecifics.validFromTimestamp,
            ),
            // Read-only field copied from original API response
            RedeemingEntity:
              updatedInstrumentTypeSpecifics[schema.apiDataKey]
                ?.RedeemingEntity,
          };
          break;
        case INSTRUMENT_TYPE_LOYALTY:
        case INSTRUMENT_TYPE_REWARD:
          requestTypeSpecifics = {
            ProgramName: updatedInstrumentTypeSpecifics.programName,
            Details: updatedInstrumentTypeSpecifics.details,
            ExpirationTimestamp: this.convertValidDateToNanosecondTimestamp(
              updatedInstrumentTypeSpecifics.expirationTimestamp,
            ),
            TransfersPermitted:
              updatedInstrumentTypeSpecifics.transfersPermitted,
            AgeRestriction: {
              Lower: updatedInstrumentTypeSpecifics.ageRestriction?.lower,
              Upper: updatedInstrumentTypeSpecifics.ageRestriction?.upper,
            },
          };
          break;
        case INSTRUMENT_TYPE_CASINO: {
          const faceValue = this.convertInstrumentQuantityToFaceValue(
            updatedInstrumentTypeSpecifics.faceValue,
          );

          requestTypeSpecifics = {
            CasinoName: updatedInstrumentTypeSpecifics.casinoName,
            UseType: updatedInstrumentTypeSpecifics.useType,
            FaceValue: {
              Value: faceValue?.units,
              CurrencyCode: faceValue?.currency,
              Precision: faceValue?.unitPrecision,
            },
            ExpirationTimestamp: this.convertValidDateToNanosecondTimestamp(
              updatedInstrumentTypeSpecifics.expirationTimestamp,
            ),
            TransfersPermitted:
              updatedInstrumentTypeSpecifics.transfersPermitted,
            AgeRestriction: {
              Lower: updatedInstrumentTypeSpecifics.ageRestriction?.lower,
              Upper: updatedInstrumentTypeSpecifics.ageRestriction?.upper,
            },
          };
          break;
        }
        case INSTRUMENT_TYPE_TICKET:
          requestTypeSpecifics = {
            EventName: updatedInstrumentTypeSpecifics.eventName,
            Venue: updatedInstrumentTypeSpecifics.venue,
            EventStartTimestamp: this.convertValidDateToNanosecondTimestamp(
              updatedInstrumentTypeSpecifics.eventStartTimestamp,
            ),
            EventEndTimestamp: this.convertValidDateToNanosecondTimestamp(
              updatedInstrumentTypeSpecifics.eventEndTimestamp,
            ),
            Area: updatedInstrumentTypeSpecifics.area,
            Section: updatedInstrumentTypeSpecifics.section,
            Row: updatedInstrumentTypeSpecifics.row,
            Seat: updatedInstrumentTypeSpecifics.seat,
            Details: updatedInstrumentTypeSpecifics.details,
            TransfersPermitted:
              updatedInstrumentTypeSpecifics.transfersPermitted,
            AgeRestriction: {
              Lower: updatedInstrumentTypeSpecifics.ageRestriction?.lower,
              Upper: updatedInstrumentTypeSpecifics.ageRestriction?.upper,
            },
          };
          break;
        default:
          throw new Error(
            `Instrument type ${updatedDetails.instrumentType} not supported`,
          );
      }

      const response = await this.tokenizedApi.endpoints.put(
        urlPath(
          'profiles',
          profileId,
          'contracts',
          contractId,
          'instruments',
          instrumentId,
        ),
        {
          details: requestDetails,
          payload: requestTypeSpecifics,
        },
      );

      let updatedInstrument = schema.normalizeResponse(
        'Update contract instrument',
        response?.data?.data,
        restApiInstrument,
      )?.normalizedValue;
      if (updatedInstrument) {
        updatedInstrument = this.transformInstrument(updatedInstrument);
        const updatedInstrumentId = updatedInstrument.instrumentId;
        const updatedDraftId = updatedInstrument.draft?.id;
        const isUpdatedInstrument = (instrument) =>
          (updatedInstrumentId &&
            instrument?.instrumentId === updatedInstrumentId) ||
          (updatedDraftId && instrument?.draft?.id === updatedDraftId);

        this.contractInstrumentsQuery.setQueryData(
          ({ instruments, idMap }) => {
            return {
              instruments: instruments.map((instrument) =>
                isUpdatedInstrument(instrument)
                  ? updatedInstrument
                  : instrument,
              ),
              idMap: new Map(
                [...idMap.entries()].map(([id, instrument]) =>
                  isUpdatedInstrument(instrument)
                    ? [id, updatedInstrument]
                    : [id, instrument],
                ),
              ),
            };
          },
          { profileId, contractId },
        );
      }
    } else {
      const updatedDetails = {
        ...contract?.draft?.details,
        ...fieldValues,
      };
      const requestDetails = {
        // These fields can be modified
        ContractName: updatedDetails.contractName,
        BodyOfAgreementType: updatedDetails.bodyOfAgreementType,
        ContractExpiration: this.convertValidDateToNanosecondTimestamp(
          updatedDetails.contractExpiration,
        ),
        GoverningLaw: updatedDetails.governingLaw,
        Jurisdiction: updatedDetails.jurisdiction,
        // These fields are read only for now, so get copied directly from the original API response
        BodyOfAgreement: updatedDetails[schema.apiDataKey]?.BodyOfAgreement,
        SupportingDocs: updatedDetails[schema.apiDataKey]?.SupportingDocs,
        ContractURI: updatedDetails[schema.apiDataKey]?.ContractURI,
        Issuer: updatedDetails[schema.apiDataKey]?.Issuer,
        ContractOperatorIncluded:
          updatedDetails[schema.apiDataKey]?.ContractOperatorIncluded,
        ContractFee: updatedDetails[schema.apiDataKey]?.ContractFee,
        VotingSystems: updatedDetails[schema.apiDataKey]?.VotingSystems,
        ContractPermissions:
          updatedDetails[schema.apiDataKey]?.ContractPermissions,
        RestrictedQtyInstruments:
          updatedDetails[schema.apiDataKey]?.RestrictedQtyInstruments,
        AdministrationProposal:
          updatedDetails[schema.apiDataKey]?.AdministrationProposal,
        HolderProposal: updatedDetails[schema.apiDataKey]?.HolderProposal,
        Oracles: updatedDetails[schema.apiDataKey]?.Oracles,
        MasterAddress: updatedDetails[schema.apiDataKey]?.MasterAddress,
        EntityContract: updatedDetails[schema.apiDataKey]?.EntityContract,
        OperatorEntityContract:
          updatedDetails[schema.apiDataKey]?.OperatorEntityContract,
        ContractType: updatedDetails[schema.apiDataKey]?.ContractType,
        Services: updatedDetails[schema.apiDataKey]?.Services,
        AdminIdentityCertificates:
          updatedDetails[schema.apiDataKey]?.AdminIdentityCertificates,
      };

      if (
        updatedDetails.bodyOfAgreementType === 2 &&
        updatedDetails[schema.apiDataKey]?.BodyOfAgreementType !== 2
      ) {
        // Have to treat creation of a body of agreement as a special
        // case to avoid a server validation error
        await this.updateContractDraft(
          profileId,
          contractId,
          requestDetails,
          [],
          [],
        );
      } else {
        await this.updateContractDraft(profileId, contractId, requestDetails);
      }
    }
  }

  modifyProtocolClausesToEnsureUpdate(clauses) {
    let modified = false;
    const newClauses = clauses?.map?.((childClause) => {
      if (modified) {
        return childClause;
      }
      if (childClause.Title) {
        modified = true;
        return {
          ...childClause,
          Title: `${childClause.Title} `,
        };
      }
      if (childClause.Body) {
        modified = true;
        return {
          ...childClause,
          Body: `${childClause.Body} `,
        };
      }
      if (childClause.Preamble) {
        modified = true;
        return {
          ...childClause,
          Preamble: `${childClause.Preamble} `,
        };
      }
      // Note: never modify the Term text, to avoid
      // triggering validation errors
      if (childClause.Definition) {
        modified = true;
        return {
          ...childClause,
          Definition: `${childClause.Definition} `,
        };
      }
      if (childClause.Children) {
        const modifiedChildren = this.modifyProtocolClausesToEnsureUpdate(
          childClause.Children,
        );
        if (modifiedChildren !== childClause.Children) {
          modified = true;
          return {
            ...childClause,
            Children: modifiedChildren,
          };
        }
      }
      if (childClause.Articles) {
        const modifiedChildren = this.modifyProtocolClausesToEnsureUpdate(
          childClause.Articles,
        );
        if (modifiedChildren !== childClause.Articles) {
          modified = true;
          return {
            ...childClause,
            Articles: modifiedChildren,
          };
        }
      }
      return childClause;
    });
    if (modified) {
      return newClauses;
    }
    return clauses;
  }

  async updateContractDraft(
    profileId,
    contractId,
    details,
    chapters,
    definitions,
    forceProseUpdate,
  ) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }

    const currentApiDetails = contract?.draft?.details?.[schema.apiDataKey];
    const currentApiChapters =
      contract?.draft?.bodyOfAgreement?.details?.[schema.apiDataKey]?.Chapters;
    const currentApiDefinitions =
      contract?.draft?.bodyOfAgreement?.details?.[schema.apiDataKey]
        ?.Definitions;

    const detailsChanged = details && !deepEqual(details, currentApiDetails);
    const chaptersChanged =
      chapters && !deepEqual(chapters, currentApiChapters);
    const definitionsChanged =
      definitions && !deepEqual(definitions, currentApiDefinitions);

    if (
      !forceProseUpdate &&
      !detailsChanged &&
      !chaptersChanged &&
      !definitionsChanged
    ) {
      return;
    }

    if (forceProseUpdate) {
      await this.tokenizedApi.endpoints.put(
        urlPath('profiles', profileId, 'contracts', contractId),
        {
          details: details || currentApiDetails,
          body_of_agreement: {
            Chapters: this.modifyProtocolClausesToEnsureUpdate(
              chapters || currentApiChapters,
            ),
            Definitions: this.modifyProtocolClausesToEnsureUpdate(
              definitions || currentApiDefinitions,
            ),
          },
        },
      );
    }

    const [response, { profile, entity } = {}] = await Promise.all([
      this.tokenizedApi.endpoints.put(
        urlPath('profiles', profileId, 'contracts', contractId),
        {
          details: details || currentApiDetails,
          body_of_agreement:
            forceProseUpdate || chaptersChanged || definitionsChanged
              ? {
                  Chapters: chaptersChanged ? chapters : currentApiChapters,
                  Definitions: definitionsChanged
                    ? definitions
                    : currentApiDefinitions,
                }
              : undefined,
        },
      ),
      allowToFail(async () => {
        const profiles = await this.tokenizedApi.entities.profilesQuery.fetch();
        const profile = profiles.idMap.get(profileId);
        const entity = await this.tokenizedApi.entities.entityQuery.fetch(
          profileId,
          profile.entityId,
        );
        return {
          profile,
          entity,
        };
      }, 'update contract draft getting profile/entity'),
    ]);

    const updatedContract = schema.normalizeResponse(
      'Update contract',
      response?.data?.data,
      restApiContract,
    ).normalizedValue;

    if (updatedContract) {
      const transformedContract = this.transformContract(updatedContract, {
        profile,
        entity,
      });

      this.contractQuery.setQueryData(transformedContract, {
        profileId,
        contractId,
      });
    }
  }

  async discardContractAmendments(profileId, contractId) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }
    if (!contract?.isPosted) {
      throw new Error('Contract has no amendment (still a draft)');
    }

    // 1) Reset contract details
    const contractDetailsRemoveEdits = DIFF_CONTRACT_DETAILS_FIELDS.reduce(
      (prevEdits, [field]) => ({
        ...prevEdits,
        [field]: contract.details[field],
      }),
      {},
    );
    await this.updateContractOrInstrumentFields({
      profileId,
      contractId,
      fieldValues: contractDetailsRemoveEdits,
    });

    // 2) Reset body of agreement
    if (contract.details.bodyOfAgreementType === 2) {
      await this.updateContractDraft(
        profileId,
        contractId,
        undefined,
        contract?.bodyOfAgreement?.details?.[schema.apiDataKey]?.Chapters,
        contract?.bodyOfAgreement?.details?.[schema.apiDataKey]?.Definitions,
        true,
      );
    }

    // 3) Loop over instruments
    const currentInstrumentsData =
      await this.contractInstrumentsQuery.fetchWithOptions(
        { staleTime: minutes(5) },
        { profileId, contractId },
      );
    for (const instrument of currentInstrumentsData.instruments) {
      const instrumentId = instrument.draft?.id;
      if (!instrumentId) {
        continue;
      }
      if (instrument.isExpired) {
        // Expired instruments can’t be amended
        continue;
      }

      if (instrument.isPosted) {
        // 4) Reset to posted state
        const instrumentDetailsRemoveEdits =
          RESET_INSTRUMENT_DETAILS_FIELDS.reduce(
            (prevEdits, field) => ({
              ...prevEdits,
              [field]: instrument.details[field],
            }),
            {},
          );
        await this.updateContractOrInstrumentFields({
          profileId,
          contractId,
          instrumentId,
          fieldSectionName: 'details',
          fieldValues: instrumentDetailsRemoveEdits,
        });

        const instrumentTypeSpecificsRemoveEdits =
          DIFF_INSTRUMENT_TYPE_SPECIFICS_FIELDS.reduce(
            (prevEdits, [field]) => ({
              ...prevEdits,
              [field]: instrument.instrumentTypeSpecifics[field],
            }),
            {},
          );
        await this.updateContractOrInstrumentFields({
          profileId,
          contractId,
          instrumentId,
          fieldSectionName: 'typeSpecifics',
          fieldValues: instrumentTypeSpecificsRemoveEdits,
        });
      } else {
        // 5) Delete new instrument drafts
        await this.deleteContractInstrumentDraft(
          profileId,
          contractId,
          instrumentId,
        );
      }
    }
  }

  async deleteContractDraft(profileId, contractId) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }
    if (!contract?.isDraft) {
      throw new Error('Contract is not a draft (is active)');
    }

    await this.tokenizedApi.endpoints.delete(
      urlPath('profiles', profileId, 'contracts', contractId),
    );

    this.contractsQuery.invalidate(profileId);
    this.contractQuery.reset({ profileId, contractId });
    this.contractTimelineQuery.reset({ profileId, contractId });
    this.contractInstrumentsQuery.reset({ profileId, contractId });
  }

  async deleteContractInstrumentDraft(profileId, contractId, instrumentId) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }
    const currentInstrumentsData =
      await this.contractInstrumentsQuery.fetchWithOptions(
        { staleTime: minutes(5) },
        { profileId, contractId },
      );
    const instrument = currentInstrumentsData?.idMap?.get?.(instrumentId);
    if (!instrument?.isDraft) {
      throw new Error('Instrument is not a draft (is active)');
    }
    if (currentInstrumentsData.instruments.length < 2) {
      throw new Error('Contracts must have at least one instrument');
    }

    await this.tokenizedApi.endpoints.delete(
      urlPath(
        'profiles',
        profileId,
        'contracts',
        contractId,
        'instruments',
        instrumentId,
      ),
    );

    this.contractInstrumentsQuery.invalidate({ profileId, contractId });
  }

  makeActivityEventForUnconfirmedExecute({
    profileId,
    lockboxId,
    displayContract,
    isAmendment,
    isConfirming,
    lockboxes,
    ownHandle,
  }) {
    return this.tokenizedApi.activity.transformEvent(
      {
        id: '',
        lockboxId,
        activityEventType: isAmendment
          ? 'contract_amendment'
          : 'contract_formation',
        stage: isConfirming ? 'confirming' : 'creating',
        isInitiator: true,
        contract: {
          contractAddress: displayContract?.draft?.id,
          name: displayContract?.formatted?.draftName,
        },
        instruments:
          displayContract?.instruments &&
          displayContract.instruments.map((instrument) => ({
            nameFromDraft: instrument?.formatted?.draftName,
            instrumentId: instrument?.instrumentId || instrument?.draft?.id,
            unitsTotal: instrument?.draft?.details?.authorizedQuantity,
            unitsDelta: instrument?.draft?.details?.authorizedQuantity,
          })),
        transactions: [
          {
            // This is the minimum necessary to
            // get the transform to identify this as a
            // proposal. Won’t be looked at for non-multisig.
            isProposer: true,
            // So that the activity details timeline can show the signers
            signers: lockboxId && lockboxes?.idMap?.get(lockboxId)?.signers,
          },
        ],
        fees: {},
      },
      { profileId, lockboxes, ownHandle },
    );
  }

  async prepareExecuteContract(profileId, contractId) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }

    await this.tokenizedApi.contractProse.updateAllEditedLinks(
      profileId,
      contractId,
      contract,
    );

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();
    const data = {
      handshake_client_id: clientId,
      handshake_signing: true,
    };

    const response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'contracts', contractId, 'finalize'),
      data,
    );
    const { normalizedValue } = schema.normalizeResponse(
      'Execute contract',
      response.data.data,
      restApiNewActivityCreated,
    );

    return normalizedValue;
  }

  async executeContract(profileId, contractId, setMfaRequest) {
    const { handshakeId, activityId } = await this.prepareExecuteContract(
      profileId,
      contractId,
    );
    if (!activityId) {
      throw new Error('The contract signing operation could not be started');
    }

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(
        handshakeId,
        setMfaRequest,
        activityId,
      );
    }

    return activityId;
  }

  async terminateContract(profileId, contractId) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }
    if (!contract?.isPosted) {
      throw new Error('Contract is not yet active');
    }

    const rollback = {
      contractExpiration: contract.draft?.details?.contractExpiration,
    };

    await this.updateContractOrInstrumentFields({
      profileId,
      contractId,
      fieldValues: {
        contractExpiration: Date.now(),
      },
    });
    try {
      await this.executeContract(profileId, contractId);
    } catch (error) {
      await this.updateContractOrInstrumentFields({
        profileId,
        contractId,
        fieldValues: rollback,
      });
      throw error;
    }
  }

  async terminateContractInstrument(profileId, contractId, instrumentId) {
    const contract = await this.contractQuery.fetchWithOptions(
      { staleTime: minutes(5) },
      { profileId, contractId },
    );
    if (!contract?.isEditable) {
      throw new Error('Contract is not editable');
    }
    if (!contract?.isPosted) {
      throw new Error('Contract is not yet active');
    }
    const currentInstrumentsData =
      await this.contractInstrumentsQuery.fetchWithOptions(
        { staleTime: minutes(5) },
        { profileId, contractId },
      );
    const instrument = currentInstrumentsData?.idMap?.get?.(instrumentId);
    if (!instrument?.isPosted || instrument?.isExpired) {
      throw new Error('Instrument is not active');
    }

    let rollback;
    if (instrument.draft?.details?.instrumentType === INSTRUMENT_TYPE_TICKET) {
      // Special case for event tickets: removing the event’s
      // timestamps marks it as inactive
      rollback = {
        eventStartTimestamp:
          instrument.draft.instrumentTypeSpecifics?.eventStartTimestamp,
        eventEndTimestamp:
          instrument.draft.instrumentTypeSpecifics?.eventEndTimestamp,
      };
      await this.updateContractOrInstrumentFields({
        profileId,
        contractId,
        instrumentId,
        fieldSectionName: 'typeSpecifics',
        fieldValues: {
          eventStartTimestamp: null,
          eventEndTimestamp: null,
        },
      });
    } else {
      // Currently, all the other instrument types have an ExpirationTimestamp,
      // which we can set to the current time to mark the instrument inactive
      rollback = {
        expirationTimestamp:
          instrument.draft.instrumentTypeSpecifics?.expirationTimestamp,
      };
      await this.updateContractOrInstrumentFields({
        profileId,
        contractId,
        instrumentId,
        fieldSectionName: 'typeSpecifics',
        fieldValues: {
          expirationTimestamp: Date.now(),
        },
      });
    }

    try {
      await this.executeContract(profileId, contractId);
    } catch (error) {
      await this.updateContractOrInstrumentFields({
        profileId,
        contractId,
        instrumentId,
        fieldSectionName: 'typeSpecifics',
        fieldValues: rollback,
      });
      throw error;
    }
  }

  instrumentAuthorizedDescriptions = defineMessages({
    default: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.create',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {token} other {tokens}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CREDIT_NOTE]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.create.creditNote',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{amount}</number> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.create.coupon',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {coupon} other {coupons}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.create.loyalty',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.create.reward',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.create.casino',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {chip} other {chips}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.create.ticket',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {ticket} other {tickets}}</type> authorized · <name>{instrument}</name>',
    },
  });

  instrumentAddedDescriptions = defineMessages({
    default: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.additional',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> additional <type>{quantity, plural, one {token} other {tokens}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CREDIT_NOTE]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.additional.creditNote',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{amount}</number> additional <type>credit</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.additional.coupon',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> additional <type>{quantity, plural, one {coupon} other {coupons}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.additional.loyalty',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> additional <type>{quantity, plural, one {point} other {points}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.additional.reward',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> additional <type>{quantity, plural, one {point} other {points}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.additional.casino',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> additional <type>{quantity, plural, one {chip} other {chips}}</type> authorized · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.additional.ticket',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> additional <type>{quantity, plural, one {ticket} other {tickets}}</type> authorized · <name>{instrument}</name>',
    },
  });

  instrumentRevokedDescriptions = defineMessages({
    default: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.revoke',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {token} other {tokens}}</type> revoked · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CREDIT_NOTE]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.revoke.creditNote',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{amount}</number> <type>credit</type> revoked · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.revoke.coupon',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {coupon} other {coupons}}</type> revoked · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.revoke.loyalty',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> revoked · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.revoke.reward',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> revoked · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.revoke.casino',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {chip} other {chips}}</type> revoked · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.revoke.ticket',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {ticket} other {tickets}}</type> revoked · <name>{instrument}</name>',
    },
  });

  instrumentAmendedDescriptions = defineMessages({
    default: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.amend',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {token} other {tokens}}</type> amended · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CREDIT_NOTE]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.amend.creditNote',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{amount}</number> amended · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.amend.coupon',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {coupon} other {coupons}}</type> amended · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.amend.loyalty',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> amended · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.amend.reward',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> amended · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.amend.casino',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {chip} other {chips}}</type> amended · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.amend.ticket',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {ticket} other {tickets}}</type> amended · <name>{instrument}</name>',
    },
  });

  instrumentExpiredDescriptions = defineMessages({
    default: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.expire',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {token} other {tokens}}</type> expired · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CREDIT_NOTE]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.expire.creditNote',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{amount}</number> expired · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.expire.coupon',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {coupon} other {coupons}}</type> expired · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.expire.loyalty',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> expired · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.expire.reward',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type> expired · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.expire.casino',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {chip} other {chips}}</type> expired · <name>{instrument}</name>',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:contractTimeline.type.instrument.expire.ticket',
      description: 'Contract timeline event type name',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {ticket} other {tickets}}</type> expired · <name>{instrument}</name>',
    },
  });

  transformTimelineEvent(event) {
    let formatDescription;
    switch (event.eventType) {
      case 'contract_drafted':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.drafted',
            description: 'Contract timeline event type name',
            defaultMessage: 'Draft created',
          });
        break;
      case 'contract_proposed':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.proposed',
            description: 'Contract timeline event type name',
            defaultMessage: 'Contract proposed',
          });
        break;
      case 'contract_canceled':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.contract_canceled',
            description: 'Contract timeline event type name',
            defaultMessage: 'Proposal canceled',
          });
        break;
      case 'contract_denied':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.contract_denied',
            description: 'Contract timeline event type name',
            defaultMessage: 'Proposal blocked',
          });
        break;
      case 'contract_form':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.contract_form',
            description: 'Contract timeline event type name',
            defaultMessage: 'Contract executed',
          });
        break;
      case 'body_form':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.body_form',
            description: 'Contract timeline event type name',
            defaultMessage: 'Body of agreement added',
          });
        break;
      case 'amend_proposed':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.amend_proposed',
            description: 'Contract timeline event type name',
            defaultMessage: 'Amendment proposed',
          });
        break;
      case 'amend_canceled':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.amend_canceled',
            description: 'Contract timeline event type name',
            defaultMessage: 'Amendment canceled',
          });
        break;
      case 'amend_denied':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.amend_denied',
            description: 'Contract timeline event type name',
            defaultMessage: 'Amendment blocked',
          });
        break;
      case 'contract_amend':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.contract_amend',
            description: 'Contract timeline event type name',
            defaultMessage: 'Contract amended',
          });
        break;
      case 'body_amend':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.body_amend',
            description: 'Contract timeline event type name',
            defaultMessage: 'Body of agreement amended',
          });
        break;
      case 'contract_expire':
        formatDescription = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:contractTimeline.type.contract_expire',
            description: 'Contract timeline event type name',
            defaultMessage: 'Contract ended',
          });
        break;
      case 'instrument_create':
        formatDescription = this.makeFormatTimelineInstrumentEventDescription({
          event,
          message:
            this.instrumentAuthorizedDescriptions[event.instrumentType] ||
            this.instrumentAuthorizedDescriptions.default,
          quantity: event.authorizedQuantity?.total || 0,
        });
        break;
      case 'instrument_modify': {
        let message;
        let quantity = event.authorizedQuantity?.total || 0;
        if (event.authorizedQuantity?.delta > 0) {
          quantity = event.authorizedQuantity.delta;
          message =
            this.instrumentAddedDescriptions[event.instrumentType] ||
            this.instrumentAddedDescriptions.default;
        } else if (event.authorizedQuantity?.delta < 0) {
          quantity = -event.authorizedQuantity.delta;
          message =
            this.instrumentRevokedDescriptions[event.instrumentType] ||
            this.instrumentRevokedDescriptions.default;
        } else {
          message =
            this.instrumentAmendedDescriptions[event.instrumentType] ||
            this.instrumentAmendedDescriptions.default;
        }
        formatDescription = this.makeFormatTimelineInstrumentEventDescription({
          event,
          message,
          quantity,
        });
        break;
      }
      case 'instrument_expire':
        formatDescription = this.makeFormatTimelineInstrumentEventDescription({
          event,
          message:
            this.instrumentExpiredDescriptions[event.instrumentType] ||
            this.instrumentExpiredDescriptions.default,
          quantity: -(event.authorizedQuantity?.delta || 0),
        });
        break;
      default:
        break;
    }
    return {
      ...event,
      formatted: {
        formatDescription,
      },
    };
  }

  instrumentsNeededForContractTimelinePreparation(events) {
    const instrumentIds = new Set();

    events?.forEach?.((event) => {
      if (event.instrumentId) {
        instrumentIds.add(event.instrumentId);
      }
    });

    return instrumentIds;
  }

  prepareContractTimelineForRender({ events, intl, instrumentsById }) {
    return events?.map?.((event) => {
      const description = event.formatted?.formatDescription?.(
        intl,
        instrumentsById,
        false,
      );
      const descriptionParts = event.formatted?.formatDescription?.(
        intl,
        instrumentsById,
        true,
      );
      return {
        ...event,
        formatted: {
          ...event.formatted,
          description,
          ...(descriptionParts !== description ? { descriptionParts } : {}),
        },
      };
    });
  }

  makeFormatTimelineInstrumentEventDescription({ event, message, quantity }) {
    return (intl, instrumentsById, outputParts) => {
      const instrument = instrumentsById?.get?.(event.instrumentId);

      const instrumentName = instrument?.formatInstrumentName();

      let amount = `${quantity}`; // fallback
      if (instrument?.instrumentType === INSTRUMENT_TYPE_CREDIT_NOTE) {
        const formattedAmount = instrument
          .withQuantity({ quantity })
          .formatAmountParts({ showCurrencyCodeOnRight: true })?.textOnly;
        if (formattedAmount) {
          amount = formattedAmount;
        }
      }

      if (outputParts) {
        let descriptionParts = intl.formatMessage(message, {
          quantity,
          amount,
          instrument: instrumentName,
          number: (formatted) => ({
            type: 'number',
            number: quantity,
            NumberFormatOptions: {},
            value: flattenFormattedParts(formatted),
          }),
          type: (formatted) => ({
            type: 'tokenType',
            value: flattenFormattedParts(formatted),
          }),
          name: (formatted) => ({
            type: 'instrument',
            instrumentId: event.instrumentId,
            value: flattenFormattedParts(formatted),
          }),
        });
        if (Array.isArray(descriptionParts)) {
          descriptionParts = descriptionParts.map((part) =>
            typeof part === 'string' ? { type: 'literal', value: part } : part,
          );
        } else {
          descriptionParts = [descriptionParts];
        }
        return descriptionParts;
      } else {
        return intl.formatMessage(message, {
          quantity,
          amount,
          instrument: instrumentName,
          number: (formatted) => formatted,
          type: (formatted) => formatted,
          name: (formatted) => formatted,
        });
      }
    };
  }

  makeFormatContractName(contract) {
    return ({ intl, preferDraftName } = {}) => {
      intl = intl || this.getIntl();

      if (!contract) {
        return intl.formatMessage(
          FORMAT_CONTRACT_NAME_UNTITLED[INSTRUMENT_TYPE_UNKNOWN],
        );
      }
      if (preferDraftName) {
        if (contract.draft?.details?.contractName) {
          return contract.draft.details.contractName;
        }
      }
      if (contract.name) {
        return contract.name;
      }
      if (contract.details?.contractName) {
        return contract.details.contractName;
      }
      if (contract.draft?.details?.contractName) {
        return contract.draft.details.contractName;
      }
      if (!contract.contractType) {
        return intl.formatMessage(
          FORMAT_CONTRACT_NAME_UNTITLED[INSTRUMENT_TYPE_UNKNOWN],
        );
      }
      if (FORMAT_CONTRACT_NAME_UNTITLED[contract.contractType]) {
        return intl.formatMessage(
          FORMAT_CONTRACT_NAME_UNTITLED[contract.contractType],
        );
      }
      return intl.formatMessage(FORMAT_CONTRACT_NAME_UNTITLED_DEFAULT, {
        contract_type: contract.contractType,
      });
    };
  }

  makeFormatContractType(contract) {
    return (intl) => {
      intl = intl || this.getIntl();

      if (!contract?.contractType) {
        return intl.formatMessage(
          FORMAT_CONTRACT_TYPE[INSTRUMENT_TYPE_UNKNOWN],
        );
      }
      if (FORMAT_CONTRACT_TYPE[contract.contractType]) {
        return intl.formatMessage(FORMAT_CONTRACT_TYPE[contract.contractType]);
      }
      return intl.formatMessage(FORMAT_CONTRACT_TYPE_DEFAULT, {
        contract_type: contract.contractType,
      });
    };
  }

  makeFormatContractInstrumentSummary(contract) {
    return ({ intl, count } = {}) => {
      intl = intl || this.getIntl();
      count = count || 0;

      if (!contract?.contractType) {
        return intl.formatMessage(
          FORMAT_CONTRACT_INSTRUMENTS_SUMMARY[INSTRUMENT_TYPE_UNKNOWN],
          {
            count,
          },
        );
      }
      if (FORMAT_CONTRACT_INSTRUMENTS_SUMMARY[contract.contractType]) {
        return intl.formatMessage(
          FORMAT_CONTRACT_INSTRUMENTS_SUMMARY[contract.contractType],
          {
            count,
          },
        );
      }
      return intl.formatMessage(FORMAT_CONTRACT_INSTRUMENTS_SUMMARY_DEFAULT, {
        contract_type: contract.contractType,
        count,
      });
    };
  }

  makeFormatInstrumentName(postedInstrument) {
    return ({
      intl,
      preferDraftName,
      otherInstrumentsById,
      fallbackName,
      noFallback,
    } = {}) => {
      intl = intl || this.getIntl();

      let instrument = postedInstrument;
      if (
        postedInstrument?.draft?.instrumentTypeSpecifics &&
        (preferDraftName || !postedInstrument?.instrumentTypeSpecifics)
      ) {
        instrument = postedInstrument.draft;
      }

      // This deals with two special cases:
      // - Digital currencies (CCY) can have localized names (“Pounds sterling” -> “Livres sterling”)
      // - Credit notes (CRN) have generated (and localized) names ("Pounds sterling credit")
      const currencyDetails = otherInstrumentsById?.get(
        instrument?.instrumentTypeSpecifics?.currencyCode ||
          instrument?.instrumentTypeSpecifics?.faceValue?.currency,
      )?.currencyDetails;

      return formatInstrumentName({
        intl,
        instrumentDetails: {
          ...instrument,
          instrumentType: instrument?.details?.instrumentType,
        },
        currencyDetails,
        fallbackName,
        noFallback,
      });
    };
  }

  makeFormatInstrumentType(instrument) {
    return (intl) => {
      return formatInstrumentTypeName(
        instrument?.instrumentType,
        intl || this.getIntl(),
      );
    };
  }
}

export default Contracts;
